import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useForm, useFieldArray } from 'react-hook-form'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { Button } from '../../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card'
import { Users, Plus, X, Mail } from 'lucide-react'

function InviteTeam({ facilityName, onSubmit, onSkip }) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    control,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: {
      invites: [{ email: '' }]
    }
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'invites'
  })

  async function handleFormSubmit(data) {
    const emails = data.invites.filter((invite) => invite.email.trim()).map((invite) => invite.email.trim())
    if (emails.length === 0) {
      onSkip()
      return
    }

    setIsSubmitting(true)
    try {
      await onSubmit(emails)
    } catch (error) {
      setIsSubmitting(false)
    }
  }

  function addInvite() {
    append({ email: '' })
  }

  return (
    <div className="flex items-center justify-center min-h-[80vh] px-4 py-8">
      <Card className="w-full max-w-xl">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
            <Users className="w-6 h-6 text-purple-600" />
          </div>
          <CardTitle className="text-2xl font-bold">Invite Your Team</CardTitle>
          <CardDescription>Invite other coaches or directors to help manage {facilityName}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
            <div className="space-y-4">
              <Label>Team Member Emails</Label>
              {fields.map((field, index) => (
                <div key={field.id} className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="email"
                      placeholder="colleague@example.com"
                      className={`pl-10 ${errors.invites?.[index]?.email ? 'border-red-500' : ''}`}
                      {...register(`invites.${index}.email`, {
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                    />
                  </div>
                  {fields.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => remove(index)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              {errors.invites && <p className="text-sm text-red-600">Please enter valid email addresses</p>}

              <Button type="button" variant="outline" size="sm" onClick={addInvite} className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Add Another Team Member
              </Button>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
              <p className="text-sm text-purple-900">
                <strong>Note:</strong> Invited team members will receive an email with instructions to set up their
                account. They&apos;ll have full access to manage classes, students, and more.
              </p>
            </div>

            <div className="space-y-3">
              <Button type="submit" disabled={isSubmitting} className="w-full bg-purple-600 hover:bg-purple-700">
                {isSubmitting ? (
                  <>
                    <div className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending invites...
                  </>
                ) : (
                  'Send Invites & Complete Setup'
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                onClick={onSkip}
                className="w-full text-gray-600 hover:text-gray-900"
              >
                Skip for now
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

InviteTeam.propTypes = {
  facilityName: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired
}

export default InviteTeam
